import Link from 'next/link'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import Alert from 'components/Alert'
import AlgoPrice from 'components/AlgoPrice'
import Loading from 'components/Loading'
import UserThumbnail from 'components/UserThumbnail'
import { classNames, truncateAddress } from 'helpers/utilities'
import type { AxiosError } from 'axios'
import type { NfdRecordCollection, NFDAnalyticRecord } from 'api/api-client'
import { algoExplorerUrl } from 'src/data/constants'
import { BiLinkExternal, BiWallet } from 'react-icons/bi'

dayjs.extend(relativeTime)

type SalesTableProps = {
  title?: string
  description?: string
  sales: NFDAnalyticRecord[] | undefined
  totalSales: number | undefined
  sellers: NfdRecordCollection | undefined
  error: AxiosError | null
  isLoading: boolean | undefined
}

export default function SalesTable({
  title = 'Sales',
  description = 'The latest sales for your NFDs',
  sales,
  totalSales,
  sellers,
  error,
  isLoading
}: SalesTableProps) {
  const renderAddress = (address: string | undefined, link = true, className = '') => {
    if (!address) {
      return <span className="mt-1 text-sm text-gray-900">N/A</span>
    }

    const nfd = sellers?.find((user) => user.caAlgo?.some((addr) => addr === address))

    if (nfd) {
      return <UserThumbnail nfd={nfd} className="text-gray-900" link={link} />
    }

    const addr = truncateAddress(address, { array: true })

    return (
      <Link href={`/address/${address}`} passHref>
        <a
          className={classNames(
            'inline-flex items-center text-sm text-gray-900 hover:text-brand-500 font-mono',
            className
          )}
        >
          <BiWallet className="flex-shrink-0 mr-1.5 h-5 w-5 my-1.5" aria-hidden="true" />
          {addr[0]}&hellip;{addr[1]}
        </a>
      </Link>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return <Loading />
    }

    if (error) {
      return <Alert type="error" title={`Error fetching sales`} error={error} />
    }

    if (!totalSales) {
      return <Alert type="info" title="No sales" message="There are currently no sales." />
    }

    return (
      <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle md:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full border-separate" style={{ borderSpacing: 0 }}>
              <thead className="relative bg-gray-100">
                <tr>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 bg-gray-100 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 bg-gray-100 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="hidden sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                  >
                    Buyer
                  </th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 border-b border-gray-300 bg-gray-100 pl-3 pr-4 sm:pr-6 py-3.5 text-center text-sm font-semibold text-gray-900 xl:text-left"
                  >
                    <span className="xl:hidden">Txn</span>
                    <span className="hidden xl:inline">Transaction</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sales?.map((sale, i) => (
                  <tr key={`sales-record-${i}`}>
                    <td
                      className={classNames(
                        i !== totalSales - 1 ? 'border-b border-gray-200' : '',
                        'w-full max-w-0 py-4 pl-4 pr-3 text-base font-medium text-gray-900 sm:pl-6 md:w-[25vw] md:max-w-[25vw] xl:w-auto xl:max-w-none truncate'
                      )}
                    >
                      <Link href={`/name/${sale.data?.name}`}>
                        <a className="text-gray-900 hover:text-brand-500 outline-brand-500 text-base font-medium">
                          {sale.data?.name}
                        </a>
                      </Link>
                    </td>
                    <td
                      className={classNames(
                        i !== totalSales - 1 ? 'border-b border-gray-200' : '',
                        'whitespace-nowrap py-4 pl-4 pr-3 font-medium text-gray-900 sm:pl-6'
                      )}
                    >
                      <AlgoPrice price={sale.price} className="xl:hidden" />
                      <AlgoPrice price={sale.price} className="hidden xl:inline" />
                    </td>
                    <td
                      className={classNames(
                        i !== totalSales - 1 ? 'border-b border-gray-200' : '',
                        'hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell'
                      )}
                    >
                      {renderAddress(sale.data?.buyer)}
                    </td>
                    <td
                      className={classNames(
                        i !== totalSales - 1 ? 'border-b border-gray-200' : '',
                        'whitespace-nowrap py-4 pl-3 pr-4 w-16 text-center text-sm font-medium sm:pr-6 xl:w-56 xl:text-left'
                      )}
                    >
                      {sale.data?.groupID ? (
                        <a
                          href={`${algoExplorerUrl}tx/group/${encodeURIComponent(
                            sale.data?.groupID
                          )}`}
                          className="inline-flex items-center text-gray-500 hover:text-gray-900 font-medium outline-brand-500 group"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span className="hidden xl:inline">
                            {dayjs(sale.timestamp).format('lll')}
                          </span>
                          <BiLinkExternal className="m-2 xl:m-0 xl:ml-3 w-5 h-5 text-gray-400 group-hover:text-brand-500" />
                        </a>
                      ) : (
                        <span className="hidden xl:inline">
                          {dayjs(sale.timestamp).format('lll')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-6">
      <div className="sm:flex sm:items-center">
        <div className="hidden sm:block flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
          <p className="mt-2 text-sm text-gray-700">{description}.</p>
        </div>
      </div>
      <div className="mt-6 flex flex-col">{renderContent()}</div>
    </div>
  )
}
